import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { ActionButtons } from '@xing-com/crate-jobs-components-job-teaser-options';
import {
  SMALL_SCREEN_BOTTOM_SHEET_PARAM,
  SEARCH_STATE_PARAMS,
} from '@xing-com/crate-jobs-constants';
import { selectJobData } from '@xing-com/crate-jobs-helpers';
import type { JobRecommendationVisibleJob } from '@xing-com/crate-jobs-hooks';
import { useJobBookmarkToggle } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import {
  getSimilarJobFlags,
  InViewTracking,
  useJobsNwtTracking,
} from '@xing-com/crate-jobs-tracking';
import { JobTeaserListItem } from '@xing-com/crate-jobs-xinglets';
import { mediaConfined, mediaTopBarHidden } from '@xing-com/layout-tokens';
import {
  cornerRadiusXL,
  motionEasingStandard,
  motionTimeS,
  spaceL,
  spaceM,
  spaceS,
  xdlColorBackgroundSecondary,
} from '@xing-com/tokens';

import type { JobItemResultVisibleJob } from '../../helpers';
import { useJDPUrlParams } from '../../hooks';

import type { ListType } from './results-list';

type Props = {
  index: number;
  item: JobItemResultVisibleJob | JobRecommendationVisibleJob;
  type: ListType;
  isUrgent?: boolean;
};

export const Result: React.FC<Props> = ({ item, type, index, isUrgent }) => {
  const nwtTrack = useJobsNwtTracking();
  const isSearchItem = item.__typename === 'JobItemResult';
  const urlParams = useJDPUrlParams(type);
  const [isVisited, setIsVisited] = useState(false);

  const { getSearchParameter } = useSearchParameter();
  const isFilterView = !!getSearchParameter(SMALL_SCREEN_BOTTOM_SHEET_PARAM);
  const sinceDate = getSearchParameter(SEARCH_STATE_PARAMS.since);

  const job = isSearchItem
    ? item.jobDetail
    : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (item as JobRecommendationVisibleJob).job;
  const highlight = isSearchItem ? item.descriptionHighlight : undefined;
  const { isBookmarked } = selectJobData(job);
  const isNew =
    !!sinceDate &&
    new Date(sinceDate) < new Date(job.refreshedAt) &&
    !isVisited;

  const path = generatePath(ROUTES.jobDetails, {
    slug: job.slug,
  });
  const urlWithParams = `${path}${urlParams}`;

  const flags = getSimilarJobFlags({
    matchingHighlights: item.matchingHighlightsV2,
    isExternalJobAd: job.redirectsToThirdPartyUrl,
    isUrgentlyHiringJob: isUrgent,
  });

  const nwtTrackingOptions = {
    page: 'jobs_search/serp',
    eventSchema: 'searchresult',
    position: index,
    original_position: item.position,
    itemUrn: job.globalId,
    trackingToken: item.trackingToken,
    flags,
  } as const;

  const onBookmark = useJobBookmarkToggle({
    jobId: job.id,
    isBookmarked,
    nwtTrackingOptions,
  });

  const onClick = (): void => {
    if (isNew) {
      setIsVisited(true);
    }

    nwtTrack({ ...nwtTrackingOptions, event: 'opened' });
  };

  return (
    <li>
      <InViewTracking
        nwtTrackingOptions={{
          ...nwtTrackingOptions,
          event: 'entered_viewport',
        }}
        skip={isFilterView}
      >
        <Item
          job={job}
          url={urlWithParams}
          highlight={highlight}
          matchingHighlights={item.matchingHighlightsV2}
          isNew={isNew}
          onClick={onClick}
          onContextMenu={onClick}
          onAuxClick={onClick}
          data-testid="job-search-result"
          headingLevel="h2"
          aria-label={job.title}
          isUrgentJob={isUrgent}
          actions={
            <>
              <DesktopActionButtons
                iconsAfter
                isBookmarked={isBookmarked}
                onBookmark={onBookmark}
              />
              <MobileActionButtons
                iconsOnly
                isBookmarked={isBookmarked}
                onBookmark={onBookmark}
              />
            </>
          }
        />
      </InViewTracking>
    </li>
  );
};

const Item = styled(JobTeaserListItem)`
  && {
    padding-block: ${spaceM};

    @media ${mediaTopBarHidden} {
      ${({ isUrgentJob }) =>
        isUrgentJob &&
        css`
          margin-top: ${spaceL};
          margin-bottom: ${spaceM};
        `}

      padding: ${spaceL} ${spaceS};
      transition: background-color ${motionTimeS} ${motionEasingStandard};

      &:hover {
        border-radius: ${cornerRadiusXL};
        background-color: ${xdlColorBackgroundSecondary};
      }
    }
  }
`;

const MobileActionButtons = styled(ActionButtons)`
  transform: translateY(-${spaceM});

  @media ${mediaConfined} {
    display: none;
  }
`;

const DesktopActionButtons = styled(ActionButtons)`
  display: none;

  @media ${mediaConfined} {
    display: flex;
    flex-shrink: 0;
    margin-left: ${spaceL};
    transform: translateY(-${spaceM});
  }
`;
